import { FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Heading, Paragraph, Skeleton, ValidationMessage } from '@digdir/designsystemet-react';
import { ShieldLockIcon } from '@navikt/aksel-icons';
import type { ActionPlanItem } from '../../../../api/dashboardPublicApi';
import {
  useGetActionPlanTemplateQuery,
  useCreateActionPlanMutation,
} from '../../../../hooks/queries/useDashboardPublicQueries';
import { ActionPlanSubmissionUtils } from '../../../../utils/actionPlanSubmission.utils';
import type { ActionPlanChoices } from '../../logic/ActionPlanFlowManager';
import { ContributionLayout } from '../ContributionLayout/ContributionLayout';
import classes from './ActionPlanSelection.module.css';

interface ActionPlanSelectionProps {
  surveyUuid: string;
  categoryId: number;
  onSuccess: (choices: ActionPlanChoices) => void;
  onBack: () => void;
}

const MAX_ITEMS_PER_SECTION = 4;

type Section = 'commitment' | 'expectation';

type ChoiceGroupProps = {
  name: Section;
  items: ActionPlanItem[];
  selectedId: number | null;
  showError: boolean;
  onSelect: (id: number) => void;
};

/** Én gruppe med radioknapper på et kort */
const ChoiceGroup = ({ name, items, selectedId, showError, onSelect }: ChoiceGroupProps) => {
  const { t } = useTranslation();
  const errorId = `${name}-error`;

  return (
    <fieldset
      className={classes.group}
      aria-invalid={showError || undefined}
      aria-describedby={showError ? errorId : undefined}
    >
      <legend className={classes.legend}>{t(`contribution.choose.${name}.title`)}</legend>
      <Paragraph data-size="sm" className={classes.hint}>
        {t('contribution.choose.hint')}
      </Paragraph>
      <div className={classes.options}>
        {items.map((item, index) => {
          const id = `${name}-${index}`;
          return (
            <label key={item.id} htmlFor={id} className={classes.option} data-selected={selectedId === item.id}>
              <input
                id={id}
                type="radio"
                name={name}
                className="ds-input"
                checked={selectedId === item.id}
                onChange={() => onSelect(item.id)}
              />
              <span>{item.text}</span>
            </label>
          );
        })}
      </div>
      {showError && (
        <ValidationMessage id={errorId}>{t(`contribution.choose.${name}.error`)}</ValidationMessage>
      )}
    </fieldset>
  );
};

export const ActionPlanSelection = ({ surveyUuid, categoryId, onSuccess, onBack }: ActionPlanSelectionProps) => {
  const { t } = useTranslation();
  const { data: template, isLoading } = useGetActionPlanTemplateQuery(surveyUuid, categoryId);
  const [selected, setSelected] = useState<Record<Section, number | null>>({ commitment: null, expectation: null });
  const [tried, setTried] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [saveError, setSaveError] = useState(false);
  const errorSummaryRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  const commitments = template?.commitments.slice(0, MAX_ITEMS_PER_SECTION) ?? [];
  const expectations = template?.expectations.slice(0, MAX_ITEMS_PER_SECTION) ?? [];
  const missing = (['commitment', 'expectation'] as const).filter((section) => selected[section] === null);

  const { mutate: createActionPlan, isPending } = useCreateActionPlanMutation(surveyUuid, {
    onSuccess: () => {
      ActionPlanSubmissionUtils.addNewActionPlanSubmission(surveyUuid);
      onSuccess({
        commitment: commitments.find((item) => item.id === selected.commitment)?.text ?? '',
        expectation: expectations.find((item) => item.id === selected.expectation)?.text ?? '',
      });
    },
    onError: (err) => {
      setSaveError(true);
      console.error('Failed to create action plan:', err);
    },
  });

  useEffect(() => {
    if (template) headingRef.current?.focus({ preventScroll: true });
  }, [template]);

  // Fokus på feiloppsummeringen hver gang brukeren prøver å sende uten å ha valgt
  useEffect(() => {
    if (attempt > 0) errorSummaryRef.current?.focus();
  }, [attempt]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (isPending) return;
    setTried(true);
    setSaveError(false);
    if (missing.length > 0) {
      setAttempt((count) => count + 1);
      return;
    }
    createActionPlan({
      categoryId,
      commitmentItemIds: [selected.commitment!],
      expectationItemIds: [selected.expectation!],
    });
  };

  if (isLoading || !template) {
    return (
      <ContributionLayout step={2}>
        <div aria-busy="true">
          <span className={classes.srOnly}>{t('actionPlanSelection.loading')}</span>
          <Skeleton variant="rectangle" width="100%" height={420} />
        </div>
      </ContributionLayout>
    );
  }

  const showErrors = tried && missing.length > 0;

  return (
    <ContributionLayout step={2}>
      <form noValidate onSubmit={handleSubmit} className={classes.form}>
        <div className={classes.intro}>
          <Heading ref={headingRef} tabIndex={-1} level={1} data-size="xl" className={classes.title}>
            {template.category}
          </Heading>
          <Paragraph className={classes.lead}>{t('contribution.choose.intro')}</Paragraph>
        </div>

        <div className={classes.anonymous}>
          <ShieldLockIcon aria-hidden className={classes.anonymousIcon} />
          <Paragraph data-size="sm">{t('actionPlanSelection.infoBox')}</Paragraph>
        </div>

        {showErrors && (
          <div ref={errorSummaryRef} tabIndex={-1} className="ds-error-summary" aria-labelledby="choose-errors">
            <Heading id="choose-errors" level={2} data-size="2xs">
              {t('contribution.choose.errorSummary')}
            </Heading>
            <ul className="ds-list">
              {missing.map((section) => (
                <li key={section}>
                  <a href={`#${section}-0`} className="ds-link">
                    {t(`contribution.choose.${section}.error`)}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className={classes.groups}>
          <ChoiceGroup
            name="commitment"
            items={commitments}
            selectedId={selected.commitment}
            showError={tried && selected.commitment === null}
            onSelect={(id) => setSelected((current) => ({ ...current, commitment: id }))}
          />
          <ChoiceGroup
            name="expectation"
            items={expectations}
            selectedId={selected.expectation}
            showError={tried && selected.expectation === null}
            onSelect={(id) => setSelected((current) => ({ ...current, expectation: id }))}
          />
        </div>

        {saveError && (
          <Alert data-color="danger">
            <Paragraph>{t('actionPlanSelection.error')}</Paragraph>
          </Alert>
        )}

        <div className={classes.actions}>
          <Button type="button" variant="secondary" onClick={onBack} disabled={isPending}>
            {t('actionPlanSelection.actions.back')}
          </Button>
          <Button type="submit" aria-disabled={isPending || undefined}>
            {isPending ? t('actionPlanSelection.actions.submitting') : t('contribution.choose.submit')}
          </Button>
        </div>
      </form>
    </ContributionLayout>
  );
};
